import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Briefcase,
  Users,
  CalendarDays,
  BookOpen,
  TrendingUp,
  Clock,
  Plus,
  ExternalLink,
  Copy
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateBusiness, setShowCreateBusiness] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    email: "",
    phone: "",
    address: "",
    slug: ""
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    if (!user) return;
    
    try {
      // Fetch businesses
      const bizResponse = await axios.get(`${API}/businesses`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      if (bizResponse.data.length > 0) {
        const biz = bizResponse.data[0];
        setBusiness(biz);
        
        // Fetch stats
        const statsResponse = await axios.get(`${API}/businesses/${biz.business_id}/stats`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const slug = formData.slug || formData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      
      await axios.post(`${API}/businesses`, {
        ...formData,
        email: formData.email || user.email,
        slug
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success("Attività creata con successo!");
      setShowCreateBusiness(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la creazione");
    } finally {
      setCreating(false);
    }
  };

  const copyBookingLink = () => {
    const link = `${window.location.origin}/book/${business?.slug}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiato negli appunti!");
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  // Show create business prompt if no business exists
  if (!business) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto">
          <Card className="border-slate-200">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Benvenuto su PrenotaFacile!
              </CardTitle>
              <CardDescription className="text-base">
                Per iniziare, crea il profilo della tua attività
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateBusiness} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Attività *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Es. Studio Medico Rossi"
                    required
                    data-testid="business-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrizione</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrivi brevemente la tua attività..."
                    rows={3}
                    data-testid="business-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder={user?.email}
                      data-testid="business-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefono</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+39 123 456 7890"
                      data-testid="business-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address">Indirizzo</Label>
                  <Input
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Via Roma 1, 20100 Milano"
                    data-testid="business-address-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slug">URL Personalizzato</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">{window.location.origin}/book/</span>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') })}
                      placeholder="studio-rossi"
                      className="flex-1"
                      data-testid="business-slug-input"
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={creating} data-testid="create-business-btn">
                  {creating ? "Creazione in corso..." : "Crea Attività"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-content">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dashboard
            </h1>
            <p className="text-slate-500 mt-1">{business.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={copyBookingLink} data-testid="copy-link-btn">
              <Copy className="h-4 w-4 mr-2" />
              Copia Link
            </Button>
            <a href={`/book/${business.slug}`} target="_blank" rel="noopener noreferrer">
              <Button size="sm" data-testid="view-booking-page-btn">
                <ExternalLink className="h-4 w-4 mr-2" />
                Pagina Prenotazioni
              </Button>
            </a>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-slate-100 shadow-sm card-hover" data-testid="stat-services">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Servizi</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{stats?.total_services || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Briefcase className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-100 shadow-sm card-hover" data-testid="stat-staff">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Staff</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{stats?.total_staff || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-100 shadow-sm card-hover" data-testid="stat-calendars">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Calendari</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{stats?.total_calendars || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CalendarDays className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-100 shadow-sm card-hover" data-testid="stat-bookings">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Prenotazioni</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{stats?.total_bookings || 0}</p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <BookOpen className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Secondary Stats */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card className="border-slate-100 shadow-sm" data-testid="stat-today">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Prenotazioni Oggi</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.today_bookings || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-100 shadow-sm" data-testid="stat-pending">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <BookOpen className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">In Attesa</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.pending_bookings || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-100 shadow-sm" data-testid="stat-revenue">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Ricavi Totali</p>
                  <p className="text-2xl font-bold text-slate-900">€{(stats?.total_revenue || 0).toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Azioni Rapide</CardTitle>
            <CardDescription>Configura la tua attività in pochi passi</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/services')} data-testid="quick-add-service">
                <Briefcase className="h-5 w-5" />
                <span>Aggiungi Servizio</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/staff')} data-testid="quick-add-staff">
                <Users className="h-5 w-5" />
                <span>Aggiungi Staff</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/calendars')} data-testid="quick-add-calendar">
                <CalendarDays className="h-5 w-5" />
                <span>Crea Calendario</span>
              </Button>
              <Button variant="outline" className="h-auto py-4 flex-col gap-2" onClick={() => navigate('/bookings')} data-testid="quick-view-bookings">
                <BookOpen className="h-5 w-5" />
                <span>Vedi Prenotazioni</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
